/**
 * Wires the existing storefront into the cart drawer, for the drawer cohort only.
 *
 * Deliberately an INTERCEPTION layer rather than edits to product-form.js, quick-view.js and
 * theme.liquid. Every add-to-cart path in the theme funnels through the <cart-notification>
 * element's renderContents():
 *
 *   product-form.js  -> this.cartNotification.renderContents(response)
 *   quick-view       -> loads product-form.js and listens for its `addToCart` event
 *   globalAddToCart  -> globalCartNotification (theme.liquid, for Maker elements)
 *
 * Overriding that one method therefore covers all of them, and leaves the theme's own code paths
 * byte-for-byte unchanged.
 *
 * Every takeover below is gated on drawerActive(), read at call time rather than at init, so an
 * external tool (Visually) can hand the cart page back mid-session by setting the flag.
 *
 * No module system in assets/ — plain script.
 */
(function () {
  const CONFIG_ID = 'cart-drawer-config';

  const readConfig = () => {
    const el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  /** Kill switch. Set window.BrightonCartDrawerDisabled = true to hand /cart back at runtime. */
  const drawerActive = () => window.BrightonCartDrawerDisabled !== true;

  const openDrawer = () => {
    const host = document.querySelector('cart-drawer');
    if (host && typeof host.open === 'function') {
      host.open();
      return true;
    }
    return false;
  };

  /** Pulls the new cart into the drawer and shows it. */
  const refreshAndOpen = (config) => {
    const store = window.BrightonCartDrawer && window.BrightonCartDrawer.store;
    if (!store) return Promise.resolve();
    return store.refresh().then(() => {
      if (config.openOnAdd !== false && !store.state.skipOpen) openDrawer();
    });
  };

  /**
   * The header cart link stays a real <a href="/cart"> so it works without JS — and for the
   * drawer cohort /cart redirects back into the drawer anyway, so the no-JS path is correct
   * rather than merely tolerable. This just avoids the page load.
   */
  const wireHeaderTrigger = () => {
    const trigger = document.getElementById('cart-icon-bubble');
    if (!trigger || trigger.dataset.drawerWired === 'true') return;
    trigger.dataset.drawerWired = 'true';

    trigger.setAttribute('aria-haspopup', 'dialog');

    trigger.addEventListener('click', (event) => {
      if (!drawerActive()) return;
      // Let modified clicks (new tab, new window) behave normally.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      if (openDrawer()) event.preventDefault();
    });
  };

  /**
   * Replaces the notification with the drawer. Only the two methods that present the
   * notification are overridden — setActiveElement and getSectionsToRender are left alone
   * because product-form.js calls them before the request is even made.
   */
  const wireNotificationTakeover = (config) => {
    const notification = document.querySelector('cart-notification');
    if (!notification || notification.dataset.drawerTakeover === 'true') return;
    notification.dataset.drawerTakeover = 'true';

    const originalRenderContents = notification.renderContents
      ? notification.renderContents.bind(notification)
      : null;
    const originalOpen = notification.open ? notification.open.bind(notification) : null;

    notification.renderContents = function (parsedState) {
      if (!drawerActive()) {
        if (originalRenderContents) originalRenderContents(parsedState);
        return;
      }

      const store = window.BrightonCartDrawer && window.BrightonCartDrawer.store;
      if (!store) {
        // Drawer never mounted — fall back rather than swallowing the confirmation entirely.
        if (originalRenderContents) originalRenderContents(parsedState);
        return;
      }

      // Programmatic adds (GWP, gift wrap reconciliation) must not pop the drawer open.
      const suppress = store.state.skipOpen || config.openOnAdd === false;
      if (suppress) {
        store.refresh().catch(() => {});
        return;
      }
      openDrawer();
    };

    notification.open = function () {
      if (!drawerActive()) {
        if (originalOpen) originalOpen();
        return;
      }
      openDrawer();
    };
  };

  /**
   * Registers the drawer as the handler for Shopify's standard openCart action. The element is
   * already named <cart-drawer> with .open(), which the default implementation finds on its own —
   * this only adds a guard for when the drawer is switched off, which must still reach /cart.
   */
  const configureStandardAction = () => {
    const actions = window.Shopify && window.Shopify.actions;
    if (!actions || !actions.openCart || typeof actions.openCart.configure !== 'function') return false;
    if (!actions.openCart.isDefault || !actions.openCart.isDefault()) return true;

    return actions.openCart.configure({
      handler: function (defaultImplementation) {
        // With the drawer disabled, an app or AI agent calling openCart must still reach the
        // cart page rather than silently doing nothing.
        if (!drawerActive()) return defaultImplementation();
        if (openDrawer()) return true;
        return defaultImplementation();
      },
    });
  };

  /**
   * Shopify's standard-actions runtime loads asynchronously, so a single attempt on DOMContentLoaded
   * usually finds nothing. Poll briefly rather than miss the registration entirely.
   */
  const configureStandardActionWhenReady = () => {
    if (configureStandardAction()) return;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (configureStandardAction() || attempts > 40) clearInterval(timer);
    }, 150);
  };

  /**
   * Picks up cart mutations made by third-party scripts.
   *
   * The Zepto personalizer never submits its form — pplr_ajax_cart POSTs to /cart/add.js over
   * jQuery and then assigns window.location, which a theme cannot cancel. So instead of trying to
   * intercept the add, watch for it: XHR covers jQuery-based apps (Zepto, Swym, Wrapin), while the
   * drawer's own store uses fetch and is therefore never double-counted here.
   *
   * NEED TOSET ZEPTO OPTION TO sync with theme (beta)
   * 
   * Whether the drawer actually gets to open depends on the app not navigating away. Zepto's
   * "after add to cart" setting has to be set to stay on the page for this to be visible.
   */
  const wireThirdPartyAdds = (config) => {
    const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?$/;
    const nativeOpen = window.XMLHttpRequest.prototype.open;

    window.XMLHttpRequest.prototype.open = function patchedOpen(method, url) {
      if (String(method).toUpperCase() === 'POST' && CART_WRITE.test(String(url).split('?')[0])) {
        this.addEventListener('load', () => {
          if (this.status < 200 || this.status >= 300) return;
          if (!drawerActive()) return;
          refreshAndOpen(config).catch(() => {});
        });
      }
      return nativeOpen.apply(this, arguments);
    };
  };

  /**
   * Adds that would otherwise navigate.
   *
   * The XHR observer only sees apps that already add via AJAX. An app submitting a real form to
   * /cart/add navigates instead, so the customer gets the cart page and then the redirect bouncing
   * them back — two full page loads to add one item.
   *
   * Listens in the BUBBLE phase and bails on defaultPrevented, so a form whose own script already
   * handles the add (product-form.js, or an app doing its own AJAX) is left alone. Only a submit
   * genuinely about to navigate reaches this, which is also what stops a double add.
   */
  const wireNavigatingAdds = (config) => {
    const ADD_PATH = /^\/cart\/add(\.js)?$/;
    const nativeSubmit = window.HTMLFormElement.prototype.submit;

    const isAddUrl = (raw) => {
      if (!raw) return false;
      try {
        const url = new URL(raw, window.location.origin);
        return url.origin === window.location.origin && ADD_PATH.test(url.pathname);
      } catch (e) {
        return false;
      }
    };

    const sendAdd = (body) =>
      fetch('/cart/add.js', {
        method: 'POST',
        body: body,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }).then((res) => {
        if (!res.ok) throw new Error('add rejected');
        return refreshAndOpen(config);
      });

    // Re-posts the form untouched — it already carries id, quantity, properties and selling plan.
    const postForm = (form, submitter) => {
      let body;
      try {
        body = new FormData(form, submitter);
      } catch (e) {
        body = new FormData(form);
      }
      return sendAdd(body);
    };

    document.addEventListener('submit', (event) => {
      if (event.defaultPrevented || !drawerActive()) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !isAddUrl(form.getAttribute('action'))) return;

      event.preventDefault();
      // Fail open: a rejected add still has to reach the cart the way it would have without us.
      postForm(form, event.submitter).catch(() => nativeSubmit.call(form));
    });

    // A programmatic submit fires no submit event, so the prototype needs patching separately.
    window.HTMLFormElement.prototype.submit = function patchedSubmit() {
      const form = this;
      if (drawerActive() && isAddUrl(form.getAttribute('action'))) {
        postForm(form, null).catch(() => nativeSubmit.call(form));
        return;
      }
      return nativeSubmit.apply(form, arguments);
    };

    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || !drawerActive()) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      const link = event.target.closest ? event.target.closest('a[href]') : null;
      if (!link) return;
      const href = link.getAttribute('href');
      if (!isAddUrl(href)) return;

      let params;
      try {
        params = new URL(href, window.location.origin).searchParams;
      } catch (e) {
        return;
      }
      // Only a link naming a variant is an add; anything else keeps its normal behaviour.
      if (!params.get('id')) return;

      event.preventDefault();
      sendAdd(params).catch(() => { window.location.href = href; });
    });
  };

  const init = () => {
    const config = readConfig();
    if (!config || !config.enabled) return;

    wireHeaderTrigger();
    wireNotificationTakeover(config);
    wireThirdPartyAdds(config);
    wireNavigatingAdds(config);
    configureStandardActionWhenReady();

    /**
     * The header and notification are re-rendered by section updates, and the drawer app mounts
     * asynchronously, so re-apply until both are wired rather than assuming a single pass is
     * enough.
     */
    const observer = new MutationObserver(() => {
      wireHeaderTrigger();
      wireNotificationTakeover(config);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
